import type {
  ReferenceEntityType,
  ReferenceMatchResult,
  ReferenceMatchStatus,
} from 'contracts/HoraCertaImport';
import { ENTITY_LABELS, ENTITY_ORDER } from '../../constants';
import type { EntityCategoryCount, MatchOption, ReferenceEntity } from '../../types';
import { selectionKey } from '../../helpers';
import { HoraCertaKpi } from '../HoraCertaKpi';
import { HoraCertaMappingRow } from '../HoraCertaMappingRow';

interface HoraCertaMappingStepProps {
  activeEntity: ReferenceEntity;
  onActiveEntityChange: (entity: ReferenceEntity) => void;
  categoryCounts: Record<ReferenceEntity, EntityCategoryCount>;
  entityMatches: ReferenceMatchResult[];
  selections: Record<string, number>;
  editingKey: string | null;
  editingValue: string;
  savingKey: string | null;
  rowErrors: Record<string, string>;
  hasLocalSelection: (match: ReferenceMatchResult) => boolean;
  effectiveStatus: (match: ReferenceMatchResult) => ReferenceMatchStatus;
  optionsByEntity: (entityType: ReferenceEntityType) => MatchOption[];
  registerFieldRef: (key: string, element: HTMLInputElement | null) => void;
  onSearchReference: (
    entityType: ReferenceEntityType,
    search: string
  ) => Promise<void>;
  onSelectionChange: (key: string, value: number | null) => void;
  onEditingValueChange: (value: string) => void;
  onClearRowError: (key: string) => void;
  onStartEdit: (key: string, currentId?: number) => void;
  onCancelEdit: (key: string) => void;
  onSaveMapping: (match: ReferenceMatchResult, key: string) => void;
}

export const HoraCertaMappingStep = ({
  activeEntity,
  onActiveEntityChange,
  categoryCounts,
  entityMatches,
  selections,
  editingKey,
  editingValue,
  savingKey,
  rowErrors,
  hasLocalSelection,
  effectiveStatus,
  optionsByEntity,
  registerFieldRef,
  onSearchReference,
  onSelectionChange,
  onEditingValueChange,
  onClearRowError,
  onStartEdit,
  onCancelEdit,
  onSaveMapping,
}: HoraCertaMappingStepProps) => (
  <div className="mt-6 grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">
    <aside className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      <h2 className="font-GilroySemibold text-base text-slate-800">
        Mapeamento (De / Para)
      </h2>
      <p className="text-xs text-slate-500 mt-1 mb-4">
        Configure a correspondência para cada tipo de dado.
      </p>
      <div className="space-y-1.5">
        {ENTITY_ORDER.map((entity) => {
          const counts = categoryCounts[entity];
          const isActive = entity === activeEntity;
          return (
            <button
              key={entity}
              type="button"
              onClick={() => onActiveEntityChange(entity)}
              className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition ${
                isActive
                  ? 'bg-orange-50 border-l-4 border-[#EE7C2C] text-[#EE7C2C]'
                  : 'hover:bg-slate-50 text-slate-700'
              }`}
            >
              <span className="text-sm font-semibold">
                {ENTITY_LABELS[entity]}
              </span>
              <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-white border border-slate-200">
                {counts.mapped + counts.pending + counts.missing}
              </span>
            </button>
          );
        })}
      </div>
    </aside>

    <section className="lg:col-span-3 bg-white rounded-xl border border-slate-200 shadow-sm p-6">
      <h3 className="font-GilroyBold text-lg text-slate-800">
        {ENTITY_LABELS[activeEntity]}
      </h3>
      <div className="grid grid-cols-3 gap-3 my-4">
        <HoraCertaKpi
          label="Mapeados"
          value={categoryCounts[activeEntity].mapped}
          className="bg-emerald-50 text-emerald-600"
        />
        <HoraCertaKpi
          label="Pendentes"
          value={categoryCounts[activeEntity].pending}
          className="bg-amber-50 text-amber-600"
        />
        <HoraCertaKpi
          label="Não encontrados"
          value={categoryCounts[activeEntity].missing}
          className="bg-red-50 text-red-600"
          helpText="Referências da planilha com mapeamento salvo no Webcol que ficou inválido. Escolha uma nova correspondência nesta tela para resolver."
        />
      </div>
      <div className="max-h-[60vh] overflow-auto border border-slate-200 rounded-lg">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="sticky top-0 z-10 bg-slate-50 border-b border-slate-200 text-slate-700">
              <th className="py-3 px-4 w-[34%]">
                Valor na planilha (Hora Certa)
              </th>
              <th className="py-3 px-4 w-[36%]">
                {ENTITY_LABELS[activeEntity]} (Webcol)
              </th>
              <th className="py-3 px-4 w-[16%]">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {entityMatches.map((match) => {
              const key = selectionKey(match.entityType, match.normalizedValue);
              const fieldError =
                rowErrors[key] ??
                (match.status === 'invalid_mapping' &&
                !hasLocalSelection(match)
                  ? 'Mapeamento inválido. Escolha uma nova correspondência.'
                  : undefined);
              return (
                <HoraCertaMappingRow
                  key={key}
                  match={match}
                  selectionValue={selections[key]}
                  isEditing={editingKey === key}
                  editingValue={editingValue}
                  isSaving={savingKey === key}
                  fieldError={fieldError}
                  effectiveStatus={effectiveStatus(match)}
                  options={optionsByEntity(match.entityType)}
                  onSelectionChange={onSelectionChange}
                  onSearchReference={onSearchReference}
                  onEditingValueChange={onEditingValueChange}
                  onClearRowError={onClearRowError}
                  onStartEdit={onStartEdit}
                  onCancelEdit={onCancelEdit}
                  onSave={onSaveMapping}
                  registerFieldRef={registerFieldRef}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  </div>
);
